import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Home, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { getPaymentStatus } from "../services/api";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");
  
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        setPaymentData(data);
        
        if (data.payment_status === "paid") {
          setStatus("success");
        } else if (data.status === "expired") {
          setStatus("expired");
        } else if (pollCount < 5) {
          // Continue polling
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus("pending");
        }
      } catch (err) {
        console.error("Payment status check failed:", err);
        if (pollCount < 3) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center" data-testid="payment-result">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h1>
            <p className="text-gray-500">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="success-title">Payment Successful!</h1>
            <p className="text-gray-500 mb-6">
              Your booking has been confirmed and paid.
            </p>
            
            {bookingId && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-500">Booking Reference</p>
                <p className="text-xl font-mono font-bold text-gray-900">{bookingId}</p>
                {paymentData && (
                  <p className="text-sm text-green-600 mt-2">
                    €{paymentData.amount} paid
                  </p>
                )}
              </div>
            )}

            <p className="text-sm text-gray-500 mb-6">
              A confirmation email has been sent to your email address.
            </p>

            <div className="flex flex-col gap-3">
              <Link to="/">
                <Button className="w-full bg-gray-900 hover:bg-gray-800">
                  <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-12 h-12 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Processing</h1>
            <p className="text-gray-500 mb-6">
              Your payment is being processed. You'll receive an email confirmation shortly.
            </p>
            {bookingId && (
              <p className="text-sm text-gray-500 mb-6">
                Booking Reference: <strong>{bookingId}</strong>
              </p>
            )}
            <Link to="/">
              <Button className="w-full bg-gray-900 hover:bg-gray-800">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </>
        )}

        {(status === "error" || status === "expired") && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {status === "expired" ? "Session Expired" : "Payment Failed"}
            </h1>
            <p className="text-gray-500 mb-6">
              {status === "expired" 
                ? "Your payment session has expired. Please try again."
                : "We couldn't verify your payment. Please contact us if you were charged."}
            </p>
            <Link to="/">
              <Button className="w-full bg-gray-900 hover:bg-gray-800">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
